import { SegmentStrategy } from "../rules/intoSegments";
import { RuleSet } from "../rules/ruleSet";

const notSchwa = { or: ['/ɑː/', '/ɑ/', '/e/', '/i/', '/ɪ/', '/o/', '/u/'] };
const C = ':consonant';
const V = ':vowel';

/**
 * https://en.wikipedia.org/wiki/Lushootseed#Alphabet
 * https://tulaliplushootseed.com/alphabet/
 */
export const lushootseedToIpa: RuleSet = {
  preprocess: ["cleanPunctuation"],
  inputSegments: SegmentStrategy.Single,
  outputSegments: SegmentStrategy.IpaModifier,
  rules: [
    // consonants: ʔ b bʼ c cʼ č čʼ d dᶻ g gʷ h ǰ k kʼ kʷ kʼʷ l lʼ ɫ ƛʼ m mʼ n nʼ p pʼ q qʼ qʷ qʼʷ s š t tʼ w wʼ xʷ x̌ x̌ʷ y yʼ
    // just need to convert the few characters that aren't standard IPA
    { from: '?', to: 'ʔ' }, // alternate
    { from: 'bʼ', to: 'ɓ' }, { from: 'b̓', to: 'ɓ' },
    { from: 'c', to: 't͜s' },
    { from: 'č', to: 't͡ʃ' }, { from: 'č', to: 't͡ʃ' }, // 2nd is an alternate diacritic
    { from: 'dᶻ', to: 'd͜z' },
    { from: 'g', to: 'ɡ' },
    { from: 'ǰ', to: 'd͡ʒ' },
    { from: 'ɫ', to: 'ɬ' }, { from: 'ł', to: 'ɬ' },
    { from: 'ƛʼ', to: 'kʼ' }, { from: 'ƛ̕', to: 'kʼ' },  // should be /tl/, but this sounds closer
    { from: 'š', to: 'ʃ' },
    { from: 'x̌', to: 'χ' },
    { from: 'y', to: 'j' },
    // alternate ejective diacritics
    { from: '\u0313', to: 'ʼ' }, // e.g. c̓
    { from: '̕', to: 'ʼ' },
  ],
  rules2: [
    // vowels, now that consonants have been normalized
    { from: 'aa', to: 'ɑː' },
    { from: 'a', to: 'ɑ' },
    { from: 'ə', to: 'ə' },
    { from: 'ii', to: 'i' },
    { from: 'i', to: 'e', next: { or: ['/b/', '/d/', '/t͡ʃ/', '#word'] } },
    { from: 'i', to: 'ɪ', next: 'l' },
    { from: 'i', to: 'i' }, // or e or ɪ
    { from: 'uu', to: 'o' },
    { from: 'u', to: 'o', next: ['#word'] },
    { from: 'u', to: 'u', previous: { or: ['/j/', '/s/', '/ʒ/', '/d͡ʒ/', '/ʔ/'] } },
    {
      from: 'u', to: 'o', next: {
        or: [
          '/b/', '/d/', '/h/', '/j/', '/k/', '/kʼ/', '/l/', '/p/',
          '/q/', '/tɬ/', '/χ/', '/ʔ/', ':vowel']
      }
    },
    { from: 'u', to: 'u' },

    //
    // rules that are perhaps more about an accent...

    // lengthen ʔ before a consonant
    { from: 'ʔ', to: 'ʔː', next: ['consonant'] },
    // ɬ in some contexts
    { from: 'ɬ', to: 'θ', next: ['#word'] },  // ɬ > θ / _#
    { from: 'ɬ', to: 'θl', previous: ['vowel'], next: ['vowel'] }, // ɬ > θl / V_V
  ],

  phonemeLengths: { map: { ə: 120 } },

  // the Southern dialect stresses the 2nd to last syllable, with exceptions
  stressRule: {
    default: -2,
    toCheck: [
      {  // ˈbiʔːbədɑʔ, ˈt͡ʃəxʷəloʔ, qɑˈlʼqɑləχʷet͡ʃ
        which: -3,
        pattern: {
          seq: [
            V,  // V.ə.V
            { or: [{ seq: [C, '/ə/'] }, { seq: [C, C, '/ə/'] }] },
            { or: [{ seq: [C, V] }, { seq: [C, C, V] }] },
            { or: ['#word', { seq: [C, '#word'] }] },
          ]
        }
      },
      { // ˈkʼʷət͡ʃəldiʔ
        which: -3,  // ə.ə.!ə
        pattern: { seq: ['/ə/', C, '/ə/', C, C, notSchwa, C, '#word'] } // əCəCC!əC#
      },

      {  // kəˈpo, d͜zəˈdis, sqʼəˈd͜zoʔ, ɡəˈdoʔ, kʷəˈdɑd, ɡʷəˈdɪl, hədˈʔiwʼ
        which: -1,
        pattern: {
          seq: [
            '/ə/',  // ə.!ə
            { or: [{ seq: [C, notSchwa] }, { seq: [C, C, notSchwa] }] },
            { or: ['#word', { seq: [C, '#word'] }] },
          ]
        }
      }
    ]
  },
}
